import { useQuery } from '@tanstack/react-query'
import req from '@sportninja/common/api/request'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import type { PlayerWithPermission } from '@sportninja/common/services/player/playerProfile'
// Direct file import, not the playerDetails index: this hook loads with every
// player page, and the index would bring the whole admin feature with it.
import { usePlayerWithPermission } from '@sportninja/common/features/profile/player/playerDetails/hooks/usePlayerWithPermission'
import { PlayerData, PlayerHighlight } from '../types'

const selectPlayer = (response: PlayerWithPermission<PlayerData>) =>
  response.player

const fetchHighlights = async (
  playerId: string
): Promise<PlayerHighlight[]> => {
  try {
    const response = await req(`/players/${playerId}/highlights`)
    return response?.data?.length > 0 ? response.data : []
  } catch {
    return []
  }
}

/**
 * GET /players/:id — cached via React Query so re-mounts (e.g. switching
 * Profile tabs that each wrap their own PlayerProvider) reuse the same data
 * instead of refetching. Shares its request with the admin permission gate.
 */
export function usePlayerQuery(playerId: string) {
  return usePlayerWithPermission<PlayerData, PlayerData>(playerId, selectPlayer)
}

/** GET /players/:id/highlights — cached per player. */
export function usePlayerHighlightsQuery(playerId: string) {
  return useQuery<PlayerHighlight[]>({
    queryKey: queryKeys.playerHighlights.byPlayer(playerId),
    queryFn: () => fetchHighlights(playerId),
    enabled: Boolean(playerId),
  })
}
